'use client'

import { useEffect, useRef, useState } from 'react'
import { useIsClient } from 'usehooks-ts'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'
import {
  viewFaqEntry,
  getTransaction,
  getFaqInfoBox,
  transformHelpCenterFaqEntryChildren,
  FaqEntryModel,
} from '@marketplace-web/help-center/help-center-faq-entry-data'
import {
  AccessChannel,
  TrackingChannel,
  getFaqPathDataFromUrl,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { transformHelpCenterTransactionResponse } from '@marketplace-web/help-center/help-center-transactions-data'

import { helpcenterTransactionScreenView } from '@marketplace-web/help-center/help-center-home-data'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import HelpCenterFaqEntry from '../HelpCenterFaqEntry'

type Props = {
  faqEntityId: string
  transactionId: string
  faqEntry: FaqEntryModel
}

const HelpCenterFaqEntryTransactionContainer = ({
  faqEntityId,
  transactionId,
  faqEntry,
}: Props) => {
  const isClientSideRenderReady = useIsClient()
  const aiHelpQuerySuggestionsAbTest = useAbTest('ai_help_query_suggestions_web_v1')

  const faqContentRef = useRef<HTMLDivElement>(null)
  const [body, setBody] = useState(faqEntry.body)

  const { relativeUrl, searchParams } = useBrowserNavigation()
  const { track } = useTracking()

  const { access_channel, transaction_id, ch } = searchParams

  const faqEntryParentId = getFaqPathDataFromUrl(relativeUrl)?.faqEntryParentId

  const accessChannel =
    typeof access_channel === 'string' ? access_channel : AccessChannel.HcTransaction
  const trackingChannel = typeof ch === 'string' ? ch : TrackingChannel.Transaction
  const parsedTransactionId = transactionId || transaction_id?.toString()

  const {
    fetch: fetchInfoBox,
    data: infoboxData,
    isLoading: isInfoBoxLoading,
  } = useFetch(getFaqInfoBox)

  const {
    fetch: fetchTransaction,
    transformedData: transaction = null,
    isLoading: isTransactionLoading,
  } = useFetch(getTransaction, transformHelpCenterTransactionResponse)

  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  useEffect(() => {
    setBody(faqEntry.body)
  }, [faqEntry.body])

  useEffect(() => {
    if (!helpCenterSessionId) return

    track(
      viewFaqEntry({
        faqEntryId: Number(faqEntityId),
        faqEntryChannel: trackingChannel,
        faqAccessChannel: accessChannel,
        screen: 'help_center_faq',
        helpCenterSessionId,
      }),
    )
  }, [faqEntityId, trackingChannel, accessChannel, track, helpCenterSessionId])

  useEffect(() => {
    if (!helpCenterSessionId) return

    if (!transactionId) return

    track(
      helpcenterTransactionScreenView({
        helpCenterSessionId,
        transactionId,
      }),
    )
  }, [track, helpCenterSessionId, transactionId])

  useEffect(() => {
    fetchInfoBox()
  }, [fetchInfoBox])

  useEffect(() => {
    if (transactionId) fetchTransaction({ id: Number(transactionId) })
  }, [transactionId, fetchTransaction])

  const isLoading = !isClientSideRenderReady || isTransactionLoading || isInfoBoxLoading

  useEffect(() => {
    const faqContent = faqContentRef.current

    if (isLoading || !faqContent) return undefined

    const handleTabNavigationClick = (event: MouseEvent) => {
      const element = event.target

      if (!(element instanceof HTMLAnchorElement)) return
      if (!element.classList.contains('nav-link')) return
      if (!element.closest('#faq-container')) return

      const targetTabId = element.getAttribute('data-target')?.replace('#', '')
      event.preventDefault()

      if (!targetTabId) return

      // Attaching event listeners directly on the element doesn't work, for such reason we are adding global event listener on the body
      //   It may be that Event listeners are broken after rerender (haven't tested such case)
      setBody(previousBody => {
        const parser = new DOMParser()
        const dom = parser.parseFromString(previousBody, 'text/html')

        dom
          .querySelectorAll('.tab-content')
          .forEach(tab =>
            tab.id === targetTabId ? tab.classList.add('active') : tab.classList.remove('active'),
          )

        dom
          .querySelectorAll('.nav-link')
          .forEach(navigationLink =>
            navigationLink.getAttribute('data-target') === `#${targetTabId}`
              ? navigationLink.parentElement?.classList.add('active')
              : navigationLink.parentElement?.classList.remove('active'),
          )

        return dom.body.innerHTML
      })
    }

    document.body.addEventListener('click', handleTabNavigationClick)

    return () => {
      document.body.removeEventListener('click', handleTabNavigationClick)
    }
  }, [isLoading])

  const infoBoxBody = infoboxData?.infobox.body || null
  const faqQuestions = faqEntry
    ? transformHelpCenterFaqEntryChildren(
        faqEntry,
        accessChannel,
        faqEntryParentId,
        parsedTransactionId,
      )
    : null

  if (isLoading) {
    return <ContentLoader size={ContentLoader.Size.XLarge} testId="faq-entry-loader" />
  }

  return (
    <div
      className={
        aiHelpQuerySuggestionsAbTest?.variant === 'on_2'
          ? 'faq__ai-search-transactions-nosearchbar'
          : 'faq__ai-search-transactions-withsearchbar'
      }
    >
      <HelpCenterFaqEntry
        channel={trackingChannel}
        faqEntryBody={body}
        faqEntryDto={null}
        answerNotFoundId={null}
        faqQuestions={faqQuestions}
        faqRecommendedQuestions={faqEntry.topFaqs}
        faqRelatedQuestions={faqEntry.relatedFaqs}
        infoBoxBody={infoBoxBody}
        transaction={transaction}
        transformedFaqEntry={faqEntry}
        faqAccessChannel={accessChannel}
        faqEntityId={Number(faqEntityId)}
      />
    </div>
  )
}

export default HelpCenterFaqEntryTransactionContainer
